import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { InstallButton } from "@/components/InstallButton";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Globe } from "lucide-react";

const NAV = [
  { key: "acheter", to: "/acheter" },
  { key: "louer", to: "/louer" },
  { key: "investir", to: "/investir" },
  { key: "conciergerie", to: "/conciergerie" },
  { key: "gestion", to: "/gestion" },
  { key: "sejours", to: "/location-courte-duree" },
  { key: "diaspora", to: "/diaspora" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const dashPath = user
    ? user.role === "admin" || user.role === "agent" || user.role === "gestionnaire" || user.role === "concierge"
      ? "/admin"
      : user.role === "owner"
      ? "/espace-proprietaire"
      : user.role === "partenaire"
      ? "/espace-partenaire"
      : "/espace-client"
    : "/login";

  const handleLogout = async () => { await logout(); navigate("/"); };

  const LangToggle = ({ className = "" }) => (
    <button onClick={toggleLang} data-testid="lang-toggle"
      className={`flex items-center gap-1.5 rounded-lg border border-navy/15 px-2.5 py-2 text-sm font-semibold text-navy hover:bg-navy/5 ${className}`}
      title={lang === "fr" ? "Switch to English" : "Passer en français"}>
      <Globe className="h-4 w-4" /> {lang === "fr" ? "EN" : "FR"}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 glass border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex h-[74px] items-center justify-between gap-4">
          <Logo />
          <nav className="hidden xl:flex items-center gap-6">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "")}`}
                className={`link-underline text-sm font-medium transition-colors ${pathname === n.to ? "text-gold" : "text-navy/80 hover:text-navy"}`}>
                {t(`nav.${n.key}`)}
              </Link>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            <InstallButton className="h-9 px-3" />
            <LangToggle />
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenu((v) => !v)} data-testid="user-menu-btn"
                  className="flex items-center gap-2 rounded-lg border border-navy/15 px-3 py-2 text-sm font-medium text-navy hover:bg-navy/5">
                  <User className="h-4 w-4" /> {user.name?.split(" ")[0]}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {userMenu && (
                  <div className="absolute right-0 mt-2 w-52 rounded-xl border bg-white p-1.5 shadow-xl" onMouseLeave={() => setUserMenu(false)}>
                    <Link to={dashPath} onClick={() => setUserMenu(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-navy hover:bg-ivory" data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4" /> {t("actions.myspace")}
                    </Link>
                    <button onClick={handleLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-testid="logout-btn">
                      <LogOut className="h-4 w-4" /> {t("actions.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-navy hover:bg-navy/5">{t("actions.login")}</Button>
                </Link>
                <Link to="/inscription" data-testid="nav-register">
                  <Button className="bg-navy text-white hover:bg-navy-600 rounded-lg">{t("actions.register")}</Button>
                </Link>
              </>
            )}
          </div>

          <div className="xl:hidden flex items-center gap-2">
            <LangToggle />
            <button className="text-navy" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t bg-white px-4 py-4 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 text-navy hover:bg-ivory font-medium" data-testid={`mnav-${n.to.replace(/\//g, "")}`}>
              {t(`nav.${n.key}`)}
            </Link>
          ))}
          <div className="pt-2 border-t mt-2 space-y-2">
            <InstallButton className="w-full h-11" />
            {user ? (
              <>
                <Link to={dashPath} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 font-medium text-navy hover:bg-ivory">{t("actions.myspace")}</Link>
                <button onClick={handleLogout} className="block w-full text-left rounded-lg px-3 py-2.5 font-medium text-red-600 hover:bg-red-50">{t("actions.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block"><Button variant="outline" className="w-full">{t("actions.login")}</Button></Link>
                <Link to="/inscription" onClick={() => setOpen(false)} className="block"><Button className="w-full bg-navy text-white">{t("actions.register")}</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
